import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * Card — BRANDBOOK §6.2 (Sprint 28c hed_v3, 4A düz hairline).
 *
 * Variants:
 *   default     → paper bg + 1px g200 + radius 18px + shadow YOK
 *   subtle      → g100 bg + 1px g200 (KPI alt-grup, ikincil kart)
 *   interactive → paper bg + 1px g200 + hover lift + cardHover shadow
 *   inset       → krem bg + border yok (container içinde inset alan)
 *   elevated    → paper bg + lg shadow (legacy alias — modal-like rare)
 *   bordered    → default ile aynı (legacy alias)
 *
 * Composable subcomponents:
 *   <Card.Header>  — başlık + opsiyonel action
 *   <Card.Body>    — içerik
 *   <Card.Footer>  — CTA satırı
 */

const cardVariants = cva('rounded-custel-2xl transition-all duration-custel-base', {
  variants: {
    variant: {
      default: 'bg-custel-paper border border-custel-border',
      subtle: 'bg-custel-g100 border border-custel-border',
      interactive: 'bg-custel-paper border border-custel-border',
      inset: 'bg-custel-krem',
      // Legacy aliases
      elevated: 'bg-custel-paper shadow-custel-lg',
      bordered: 'bg-custel-paper border border-custel-border',
    },
    padding: {
      none: '',
      sm: 'p-3',
      md: 'p-4',
      lg: 'p-6',
    },
    interactive: {
      true: 'cursor-pointer hover:-translate-y-[3px] hover:shadow-custel-card-hover hover:border-custel-ink',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    padding: 'md',
    interactive: false,
  },
});

export interface CardProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {
  children: ReactNode;
}

const CardRoot = forwardRef<HTMLDivElement, CardProps>(
  ({ variant, padding, interactive, className, children, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant, padding, interactive }), className)} {...props}>
      {children}
    </div>
  ),
);
CardRoot.displayName = 'Card';

const CardHeader = ({
  title,
  action,
  description,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) => (
  <div className={cn('mb-3 flex items-start justify-between gap-3', className)}>
    <div className="min-w-0 flex-1">
      <h3 className="text-base font-semibold text-custel-ink">{title}</h3>
      {description && <p className="mt-0.5 text-sm text-custel-text-secondary">{description}</p>}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

const CardBody = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('text-custel-ink', className)}>{children}</div>
);

const CardFooter = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('mt-4 flex items-center justify-end gap-2', className)}>{children}</div>
);

export const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Body: CardBody,
  Footer: CardFooter,
});

export { cardVariants };
